import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@repletoryreach/sdk-auth';
import { identity } from '@repletoryreach/sdk-api';
import { Stack, Card, Title, Button, Text, Group, Badge, Code, CopyButton } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { TextInput } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import QRCode from 'qrcode';
import { useT } from '../../../../i18n/I18nProvider';

/** TOTP two-factor setup, enable/disable and recovery codes. */
export function TwoFactorPanel() {
  const t = useT();
  const { user, refresh } = useAuth();
  const enabled = user?.two_factor_enabled ?? false;

  const [secret, setSecret] = useState<string | null>(null);
  const [otpauth, setOtpauth] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [qr, setQr] = useState<string | null>(null);

  const status = useQuery({
    queryKey: ['2fa', 'status'],
    queryFn: () => identity.twoFactorStatus(),
    retry: false
  });
  const [busy, setBusy] = useState(false);

  /*
   * The QR is generated in the browser from the `otpauth://` URI, so the
   * shared secret is never sent anywhere to be rendered.
   */
  useEffect(() => {
    if (!otpauth) { setQr(null); return; }
    let cancelled = false;
    void QRCode.toDataURL(otpauth, { margin: 1, width: 336 })
      .then((url) => { if (!cancelled) setQr(url); })
      // A failed render leaves the typed secret, which still works.
      .catch(() => { if (!cancelled) setQr(null); });
    return () => { cancelled = true; };
  }, [otpauth]);

  async function startSetup() {
    setBusy(true);
    try {
      const res = await identity.twoFactorSetup();
      if (!res.data.secret) {
        // The SDK documents `secret` as null when 2FA is already set up. The
        // old code called `setSecret(null)`, the enrolment branch never
        // rendered, and the user watched the button stop spinning with no
        // change and no message.
        notifyError(t('settings.security.twoFactor.alreadySetUp'), '2FA');
        await refresh();
        return;
      }
      setSecret(res.data.secret);
      setOtpauth(res.data.otpauth_uri);
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('settings.security.twoFactor.setupFailed'), '2FA');
    } finally {
      setBusy(false);
    }
  }

  async function enable() {
    setBusy(true);
    try {
      const res = await identity.twoFactorEnable(code.trim());
      setRecoveryCodes(res.data.recovery_codes);
      setSecret(null);
      setOtpauth(null);
      setCode('');
      await refresh();
      notifySuccess(t('settings.security.twoFactor.onHint'), t('settings.security.twoFactor.toast.enabled'));
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('auth.common.codeMismatch'), '2FA');
    } finally {
      setBusy(false);
    }
  }

  /*
   * §4 item 4: the order was type a code → click Disable → confirm a red modal
   * → THEN call the API, so a wrong or expired code meant confirming something
   * frightening and only then being told the code did not match. TOTP codes
   * live 30 seconds, so the confirm step actively made failure more likely.
   *
   * The code entry IS the confirmation: someone holding a current code from
   * the authenticator has demonstrated intent. What remains is a plain
   * statement of the consequence, above the button.
   */
  function disable() {
    void disableConfirmed();
  }

  async function disableConfirmed() {
    setBusy(true);
    try {
      await identity.twoFactorDisable(code.trim());
      setCode('');
      await refresh();
      notifySuccess(t('settings.security.twoFactor.toast.disabled'), '2FA');
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('auth.common.codeMismatch'), '2FA');
    } finally {
      setBusy(false);
    }
  }

  async function regenerate() {
    setBusy(true);
    try {
      const res = await identity.twoFactorRecoveryCodes(code.trim());
      setRecoveryCodes(res.data.recovery_codes);
      setCode('');
      notifySuccess(t('settings.security.twoFactor.toast.newCodes'), '2FA');
    } catch (e) {
      notifyError(e instanceof Error ? e.message : t('auth.common.codeMismatch'), '2FA');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card withBorder data-guide="security-2fa">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={4}>{t('settings.security.twoFactor.title')}</Title>
          <Badge color={enabled ? 'green' : 'gray'} variant="light">
            {enabled ? t('common.enabled') : t('common.disabled')}
          </Badge>
        </Group>

        {recoveryCodes && (
          <Alert color="warning" title={t('settings.security.twoFactor.modal.codes.title')}>
            <Stack gap="sm">
              <Text role="bodySmall">{t('settings.security.twoFactor.modal.codes.body')}</Text>

              <div className="rr-security__codes">
                {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
              </div>

              <Group gap="xs" wrap="wrap">
                <CopyButton value={recoveryCodes.join('\n')}>
                  {({ copied, failed, copy }) => (
                    <Button size="xs" onClick={copy}>
                      {failed ? t('common.copyFailed') : copied ? t('common.copied') : t('action.copy')}
                    </Button>
                  )}
                </CopyButton>
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => downloadText(
                    recoveryCodes.join('\n'),
                    `repletoryreach-recovery-codes-${Date.now()}.txt`,
                    'text/plain'
                  )}
                >
                  {t('common.download')}
                </Button>
                <Button size="xs" variant="default" onClick={() => window.print()}>
                  {t('settings.security.twoFactor.print')}
                </Button>
                {/*
                  The gate. Regenerating codes needs a working authenticator,
                  which is exactly what someone who has lost their codes does
                  not have, so dismissing has to be deliberate.
                */}
                <Button size="xs" variant="subtle" onClick={() => setRecoveryCodes(null)}>
                  {t('settings.apiKeys.savedIt')}
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}

        {!enabled && !secret && (
          <Stack gap="sm">
            <Text role="bodySmall" c="var(--rr-color-text-muted)">
              {t('settings.security.twoFactor.intro')}
            </Text>
            <div>
              <Button loading={busy} onClick={startSetup}>{t('settings.security.twoFactor.actions.setup')}</Button>
            </div>
          </Stack>
        )}

        {!enabled && secret && (
          <Stack gap="sm">
            <Text role="bodySmall">
              {t('settings.security.twoFactor.setup.step1')}
            </Text>
            {otpauth && (
              <Group gap="md" align="flex-start" wrap="wrap">
                {/*
                  A QR code, rather than asking someone to hand-type base32
                  into a phone. Rendered from a data URI built in the browser,
                  so the secret never leaves this page.
                */}
                {qr && (
                  <img
                    className="rr-security__qr"
                    src={qr}
                    alt={t('settings.security.twoFactor.setup.qrAlt')}
                    width={168}
                    height={168}
                  />
                )}
                <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                  <Text role="caption" c="var(--rr-color-text-muted)">
                    {t('settings.security.twoFactor.setup.orType')}
                  </Text>
                  <Code block className="rr-security__secret">{secret}</Code>
                  <CopyButton value={secret}>
                    {({ copied, copy }) => (
                      <Button size="xs" variant="subtle" onClick={copy} style={{ alignSelf: 'flex-start' }}>
                        {copied ? t('common.copied') : t('action.copy')}
                      </Button>
                    )}
                  </CopyButton>
                </Stack>
              </Group>
            )}
            <Text role="bodySmall">{t('settings.security.twoFactor.setup.step2')}</Text>
            <TextInput
              label={t('settings.security.twoFactor.code')}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              inputMode="numeric"
            />
            <Group>
              <Button loading={busy} onClick={enable} disabled={!code.trim()}>{t('settings.security.twoFactor.actions.enable')}</Button>
              <Button variant="default" onClick={() => { setSecret(null); setOtpauth(null); }}>Cancel</Button>
            </Group>
          </Stack>
        )}

        {enabled && (
          <Stack gap="sm">
            {/* §4 item 12: a user who had burned eight of ten codes had no way
                to find out, and regenerating needs a working authenticator. */}
            {status.data && (
              <Text role="bodySmall" c={status.data.data.recovery_codes_remaining <= 3 ? 'var(--rr-color-warning)' : undefined}>
                {t('settings.security.twoFactor.codesRemaining', {
                  count: status.data.data.recovery_codes_remaining
                })}
              </Text>
            )}
            <Text role="bodySmall" c="var(--rr-color-text-muted)">
              {t('settings.security.twoFactor.manageHint')}
            </Text>
            <TextInput
              label={t('auth.login.twofa.form.code')}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              inputMode="numeric"
            />
            <Group>
              <Button variant="default" loading={busy} onClick={regenerate} disabled={!code.trim()}>
                {t('settings.security.twoFactor.actions.newCodes')}
              </Button>
              <Button color="red" variant="subtle" loading={busy} onClick={disable} disabled={!code.trim()}>
                {t('settings.security.twoFactor.actions.disable')}
              </Button>
            </Group>
            {/* The consequence, stated where it is read rather than inside a
                modal that appears after the code has already expired. */}
            <Text role="caption" c="var(--rr-color-text-muted)">
              {t('settings.security.twoFactor.modal.disable.body')}
            </Text>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
